<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Logo from 'next/icon/Logo.vue';
import LabelValueRow from './components/LabelValueRow.vue';
import ComparisonRow from './components/ComparisonRow.vue';
import CollaboratorSelector from './components/CollaboratorSelector.vue';
import TimeRangeSelector from './components/TimeRangeSelector.vue';
import MultiSelectFilter from './components/MultiSelectFilter.vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import liveReports from 'dashboard/api/liveReports';
import summaryReportsApi from 'dashboard/api/summaryReports';
import { getUnixStartOfDay } from 'helpers/DateHelper';
import { formatFriendlyDuration } from './durationFormat';
import { weightedAvgResolutionTime } from './teamSummaryMetrics';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const props = defineProps({
  inboxId: { type: [Number, String], default: null },
  teamIds: { type: Array, default: () => [] },
});

const SECONDS_IN_DAY = 24 * 60 * 60;
const NO_LABEL_ID = 0;
const RANGE_SECONDS = {
  '7d': 7 * SECONDS_IN_DAY,
  '30d': 30 * SECONDS_IN_DAY,
};

// "24h" aqui significa "hoje" (desde a meia-noite), não uma janela rolante
// das últimas 24 horas — pra bater com "Clientes atendidos no dia" e com o
// total de "Atendimentos finalizados" do Central. Antes esse selector usava
// janela rolante e o total somado por colaborador não batia com o Geral.
const rangeFor = key => {
  const until = Math.floor(Date.now() / 1000);
  if (key === '24h') return { since: getUnixStartOfDay(new Date()), until };
  return { since: until - RANGE_SECONDS[key], until };
};

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();

// Período e colaborador selecionado ficam em cache no localStorage por conta
// pra não resetar quando o usuário sai da tela e volta — é só cache local no
// navegador, não é salvo no backend.
const savedFilters =
  LocalStorage.getFromJsonStore(
    LOCAL_STORAGE_KEYS.REPORT_DASHBOARD_TEAM_FILTERS,
    accountId.value
  ) || {};

// "Geral" e "Colaboradores" têm filtros de período independentes — trocar um
// não pode afetar o outro. Por isso as duas seções buscam
// summary_reports/agent direto pela API (bypass do Vuex `summaryReports`,
// que só guarda um resultado por vez) com sua própria janela de tempo.
const generalRange = ref(savedFilters.generalRange || '24h');
const collaboratorRange = ref(savedFilters.collaboratorRange || '24h');

// Filtro de tags do bloco "Geral" — troca os endpoints usados pro
// equivalente com join de tags (summary_reports/label, live_reports com
// label_id). "Geral" e "Colaboradores" têm seleção de tags independente,
// mesmo padrão já usado pro período (generalRange/collaboratorRange).
const selectedGeneralLabelIds = ref(savedFilters.selectedGeneralLabelIds || []);
const hasGeneralLabelFilter = computed(
  () => selectedGeneralLabelIds.value.length > 0
);

const selectedCollaboratorLabelIds = ref(
  savedFilters.selectedCollaboratorLabelIds || []
);
const hasCollaboratorLabelFilter = computed(
  () => selectedCollaboratorLabelIds.value.length > 0
);

const generalLabelOptions = ref([]);
const fetchGeneralLabelOptions = async () => {
  const { data } = await summaryReportsApi.getLabelReports({
    inboxId: props.inboxId,
  });
  // "Sem tag" (id 0) — o backend (live_reports e summary_reports/agent)
  // trata o 0 como "conversa sem nenhuma tag", então "Marcar todos" inclui
  // essas conversas.
  generalLabelOptions.value = [
    { id: NO_LABEL_ID, name: t('REPORT_DASHBOARD.OPERATIONAL.NO_LABEL') },
    ...data.map(label => ({ id: label.id, name: label.name })),
  ];
};

const agents = computed(() => store.getters['agents/getAgents']);
const allAgents = computed(() =>
  agents.value.map(agent => ({ id: agent.id, name: agent.name }))
);

// "Atendimentos finalizados" e "Tempo médio" (Geral) vêm da soma/média
// ponderada de generalAgentSummary (summary_reports/agent), buscado com a
// janela de generalRange — independente da janela usada pelo bloco
// "Colaboradores". Usa o endpoint de agente (não o de etiqueta) mesmo com
// filtro de tags ativo: o de etiqueta soma por etiqueta, e uma conversa com
// mais de uma das tags selecionadas seria contada mais de uma vez (uma
// conversa só tem um agente, então somar por agente não tem esse problema).
// O backend já deduplica esse cruzamento — ver AgentSummaryBuilder.
const generalAgentSummary = ref([]);
const totalFinalizadosHoje = computed(() =>
  generalAgentSummary.value.reduce(
    (sum, entry) => sum + (entry.resolved_conversations_count || 0),
    0
  )
);
const avgResolutionTimeToday = computed(() =>
  formatFriendlyDuration(weightedAvgResolutionTime(generalAgentSummary.value))
);

const fetchGeneralAgentSummary = async () => {
  const { data } = await summaryReportsApi.getAgentReports({
    ...rangeFor(generalRange.value),
    inboxId: props.inboxId,
    teamId: props.teamIds,
    labelId: selectedGeneralLabelIds.value,
  });
  generalAgentSummary.value = data;
};

// "Clientes atendidos no dia" é sempre "hoje" (desde a meia-noite) — o nome
// da métrica é explícito sobre o período, então não segue nenhum dos dois
// filtros (generalRange/collaboratorRange). Usado tanto pro total do "Geral"
// quanto pro valor do colaborador selecionado, já que os dois mostram a
// mesma métrica.
const todayAgentSummary = ref([]);
const fetchTodayAgentSummary = async () => {
  const since = getUnixStartOfDay(new Date());
  const until = Math.floor(Date.now() / 1000);
  const { data } = await summaryReportsApi.getAgentReports({
    since,
    until,
    inboxId: props.inboxId,
    teamId: props.teamIds,
  });
  todayAgentSummary.value = data;
};
const atendidosDiaGeral = computed(() =>
  todayAgentSummary.value.reduce(
    (sum, entry) => sum + (entry.conversations_count || 0),
    0
  )
);

// "Atendimentos finalizados" e "Tempo médio" do colaborador selecionado vêm
// da janela de collaboratorRange, separada da janela do "Geral".
// finalizadosTotal é o denominador do percentual — soma do mesmo período
// (collaboratorRange), pra ficar consistente com o numerador.
const collaboratorAgentSummary = ref([]);
const collaboratorFinalizadosTotal = computed(() =>
  collaboratorAgentSummary.value.reduce(
    (sum, entry) => sum + (entry.resolved_conversations_count || 0),
    0
  )
);
const collaboratorFinalizadosPorTotal = computed(() =>
  collaboratorAgentSummary.value.reduce(
    (sum, entry) => sum + (entry.resolved_by_conversations_count || 0),
    0
  )
);

const fetchCollaboratorAgentSummary = async () => {
  const { data } = await summaryReportsApi.getAgentReports({
    ...rangeFor(collaboratorRange.value),
    inboxId: props.inboxId,
    teamId: props.teamIds,
    labelId: selectedCollaboratorLabelIds.value,
  });
  collaboratorAgentSummary.value = data;
};

// "Em atendimento" por colaborador não é mais mockado — reaproveita o mesmo
// endpoint já usado pela Visão Geral (grouped_conversation_metrics), só que
// agrupado por assignee_id em vez de team_id.
const agentConversationMetric = ref([]);
const fetchAgentConversationMetric = async () => {
  const { data } = await liveReports.getGroupedConversations({
    groupBy: 'assignee_id',
    inboxId: props.inboxId,
    teamIds: props.teamIds,
    labelIds: selectedCollaboratorLabelIds.value,
  });
  agentConversationMetric.value = data;
};

// "Aguardando há mais de 24h" não é mais mockado — vem de
// live_reports/waiting_more_than_24h (conversas abertas com waiting_since
// anterior a 24h atrás). Segue o filtro de tags do bloco Geral: o backend
// já filtra direto via `tagged_with` (sem o risco de contar a mesma
// conversa duas vezes que a soma local por tag teria).
const waitingMoreThan24h = ref(0);
const fetchWaitingMoreThan24h = async () => {
  const { data } = await liveReports.getWaitingMoreThan24h({
    inbox_id: props.inboxId,
    team_id: props.teamIds,
    label_id: selectedGeneralLabelIds.value,
  });
  waitingMoreThan24h.value = data.count;
};

// "Em atendimento" (Geral) não é mais mockado — vem de
// getAccountConversationMetric.open. Não depende de período (contagem "ao
// vivo" de conversas abertas), por isso não segue generalRange/collaboratorRange.
const accountConversationMetric = computed(
  () => store.getters.getAccountConversationMetric
);
const emAtendimentoGeral = computed(
  () => accountConversationMetric.value.open || 0
);

// Com filtro de tags ativo no bloco Geral, busca via live_reports/
// conversation_metrics com label_id — esse endpoint filtra com `tagged_with`
// (WHERE EXISTS), que não duplica conversas com mais de uma tag selecionada
// (ao contrário de somar open_conversations_count por etiqueta, que contaria
// a mesma conversa uma vez por tag). Sem filtro, mantém emAtendimentoGeral
// (vindo do Vuex) sem chamada extra. O comparativo do bloco Colaboradores
// continua usando emAtendimentoGeral quando o filtro de tag dele estiver
// inativo — ver emAtendimentoComparisonTotal.
const filteredEmAtendimentoGeral = ref(0);
const fetchFilteredEmAtendimentoGeral = async () => {
  if (!hasGeneralLabelFilter.value) return;
  const { data } = await liveReports.getConversationMetric({
    inbox_id: props.inboxId,
    team_id: props.teamIds,
    label_id: selectedGeneralLabelIds.value,
  });
  filteredEmAtendimentoGeral.value = data.open || 0;
};
const emAtendimentoGeralDisplay = computed(() =>
  hasGeneralLabelFilter.value
    ? filteredEmAtendimentoGeral.value
    : emAtendimentoGeral.value
);

const fetchAccountConversationMetric = () =>
  store.dispatch('fetchAccountConversationMetric', {
    inbox_id: props.inboxId,
    team_id: props.teamIds,
  });

const refreshTeamData = () =>
  Promise.all([
    fetchFilteredEmAtendimentoGeral(),
    fetchAgentConversationMetric(),
    fetchWaitingMoreThan24h(),
    fetchGeneralAgentSummary(),
    fetchTodayAgentSummary(),
    fetchCollaboratorAgentSummary(),
    fetchAccountConversationMetric(),
  ]);

const { startRefetching } = useLiveRefresh(refreshTeamData);

// Rebusca na hora quando um dos períodos muda, sem esperar o próximo ciclo
// de 60s do useLiveRefresh — cada filtro só rebusca os dados da sua própria
// seção. avgResolutionTimeToday não precisa de fetch próprio: é computed a
// partir de generalAgentSummary.
watch(generalRange, fetchGeneralAgentSummary);
watch(
  selectedGeneralLabelIds,
  () =>
    Promise.all([
      fetchGeneralAgentSummary(),
      fetchWaitingMoreThan24h(),
      fetchFilteredEmAtendimentoGeral(),
    ]),
  { deep: true }
);
watch(collaboratorRange, fetchCollaboratorAgentSummary);
watch(
  selectedCollaboratorLabelIds,
  () =>
    Promise.all([
      fetchCollaboratorAgentSummary(),
      fetchAgentConversationMetric(),
    ]),
  { deep: true }
);
watch(() => props.inboxId, refreshTeamData);
watch(() => props.teamIds, refreshTeamData, { deep: true });

onMounted(() => {
  store.dispatch('agents/get');
  refreshTeamData();
  fetchGeneralLabelOptions();
  startRefetching();
});

// A lista de colaboradores não é mais mockada (João/Maria/Carlos/Ana) — vem
// de agents/getAgents, mesma lista real usada em "Produção individual". Se já
// tem um colaborador em cache pra essa conta, mantém a escolha do usuário em
// vez de cair no primeiro da lista.
const selectedAgentId = ref(savedFilters.selectedAgentId ?? null);

watch(
  allAgents,
  list => {
    if (selectedAgentId.value === null && list.length) {
      selectedAgentId.value = list[0].id;
    }
  },
  { immediate: true }
);

watch(
  [
    generalRange,
    collaboratorRange,
    selectedAgentId,
    selectedGeneralLabelIds,
    selectedCollaboratorLabelIds,
  ],
  () => {
    LocalStorage.updateJsonStore(
      LOCAL_STORAGE_KEYS.REPORT_DASHBOARD_TEAM_FILTERS,
      accountId.value,
      {
        generalRange: generalRange.value,
        collaboratorRange: collaboratorRange.value,
        selectedAgentId: selectedAgentId.value,
        selectedGeneralLabelIds: selectedGeneralLabelIds.value,
        selectedCollaboratorLabelIds: selectedCollaboratorLabelIds.value,
      }
    );
  },
  { deep: true }
);

// "Clientes aguardando" por colaborador não tem fonte de dado disponível —
// waiting_since não é filtrável por agente em nenhum endpoint existente
// (mesma limitação do "Aguardando há mais de 24h" do Geral). Por isso essa
// métrica foi removida da seção de colaboradores em vez de continuar
// mockada anexada a um agente real.
const selected = computed(() => {
  const agent = agents.value.find(item => item.id === selectedAgentId.value);
  if (!agent) return null;

  const todaySummary =
    todayAgentSummary.value.find(entry => entry.id === agent.id) || {};
  const summary =
    collaboratorAgentSummary.value.find(entry => entry.id === agent.id) || {};
  const conversationMetric =
    agentConversationMetric.value.find(
      metric => metric.assignee_id === agent.id
    ) || {};

  return {
    nome: agent.name,
    atendidosDia: todaySummary.conversations_count || 0,
    emAtendimento: conversationMetric.open || 0,
    finalizados: summary.resolved_conversations_count || 0,
    finalizadosPor: summary.resolved_by_conversations_count || 0,
    tempoMedio: formatFriendlyDuration(summary.avg_resolution_time),
  };
});

const percentOf = (value, total) => {
  if (!total) return 0;
  return Math.round((value / total) * 100);
};

// Com filtro de tags ativo em "Colaboradores", agentConversationMetric já
// vem filtrado por tag (todos os agentes) — soma-lo dá o total "ao vivo"
// certo pra essa seleção. Sem filtro, usa o total real (emAtendimentoGeral),
// já que esse não segue o filtro de tags do bloco "Geral" (são seleções
// independentes).
const emAtendimentoComparisonTotal = computed(() => {
  if (!hasCollaboratorLabelFilter.value) return emAtendimentoGeral.value;
  return agentConversationMetric.value.reduce(
    (sum, metric) => sum + (metric.open || 0),
    0
  );
});

// "atendidosDia" e "emAtendimento" não dependem de nenhum dos dois filtros de
// período (o primeiro é sempre "hoje", o segundo é uma contagem "ao vivo"),
// por isso comparam contra os totais do "Geral" normalmente. "finalizados" e
// "finalizadosPor" seguem collaboratorRange — por isso comparam contra o
// total do próprio bloco "Colaboradores", na mesma janela.
const comparisons = computed(() => {
  if (!selected.value) return {};
  return {
    atendidosDia: percentOf(
      selected.value.atendidosDia,
      atendidosDiaGeral.value
    ),
    emAtendimento: percentOf(
      selected.value.emAtendimento,
      emAtendimentoComparisonTotal.value
    ),
    finalizados: percentOf(
      selected.value.finalizados,
      collaboratorFinalizadosTotal.value
    ),
    finalizadosPor: percentOf(
      selected.value.finalizadosPor,
      collaboratorFinalizadosPorTotal.value
    ),
  };
});
</script>

<template>
  <section
    class="relative h-full flex flex-col px-6 lg:px-16 py-6 overflow-hidden"
  >
    <div
      class="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0 items-stretch"
    >
      <div
        class="border border-n-weak rounded-lg p-5 min-w-0 overflow-y-auto no-scrollbar"
      >
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-xs uppercase tracking-widest text-n-slate-11">
            {{ t('REPORT_DASHBOARD.TEAM.GENERAL') }}
          </h2>
          <MultiSelectFilter
            v-model="selectedGeneralLabelIds"
            :items="generalLabelOptions"
            :placeholder="t('REPORT_DASHBOARD.OPERATIONAL.ALL_LABELS')"
            :empty-state-message="
              t('REPORT_DASHBOARD.OPERATIONAL.NO_LABELS_FOUND')
            "
            show-select-all
            align-right
          />
        </div>
        <TimeRangeSelector v-model="generalRange" class="mb-3" />
        <!-- Métrica pouco confiável: usa o assignee_id atual da conversa
        (join ao vivo), não um snapshot de quem atendeu. Reatribuição ou
        unassign muda o número retroativamente sem nenhum atendimento real
        ter mudado. Desativado a pedido, sem remover a lógica.
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.TEAM.CLIENTS_SERVED_TODAY')"
          :value="atendidosDiaGeral"
          :hint="t('REPORT_DASHBOARD.TEAM.HINTS.CLIENTS_SERVED_TODAY')"
        />
        -->
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.TEAM.WAITING_MORE_24H')"
          :value="waitingMoreThan24h"
          :hint="t('REPORT_DASHBOARD.TEAM.HINTS.WAITING_MORE_24H')"
        />
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.TEAM.IN_PROGRESS')"
          :value="emAtendimentoGeralDisplay"
          :hint="t('REPORT_DASHBOARD.TEAM.HINTS.IN_PROGRESS')"
        />
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.TEAM.RESOLVED_CONVERSATIONS')"
          :value="totalFinalizadosHoje"
          :hint="t('REPORT_DASHBOARD.TEAM.HINTS.RESOLVED_CONVERSATIONS')"
        />
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.TEAM.AVG_RESOLUTION_TIME')"
          :value="avgResolutionTimeToday"
          :hint="t('REPORT_DASHBOARD.TEAM.HINTS.AVG_RESOLUTION_TIME')"
        />
      </div>

      <div
        class="border border-n-weak rounded-lg p-5 min-w-0 overflow-y-auto no-scrollbar"
      >
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-xs uppercase tracking-widest text-n-slate-11">
            {{ t('REPORT_DASHBOARD.TEAM.COLLABORATORS') }}
          </h2>
          <MultiSelectFilter
            v-model="selectedCollaboratorLabelIds"
            :items="generalLabelOptions"
            :placeholder="t('REPORT_DASHBOARD.OPERATIONAL.ALL_LABELS')"
            :empty-state-message="
              t('REPORT_DASHBOARD.OPERATIONAL.NO_LABELS_FOUND')
            "
            show-select-all
            align-right
          />
        </div>
        <CollaboratorSelector
          v-model="selectedAgentId"
          :collaborators="allAgents"
        />
        <TimeRangeSelector v-model="collaboratorRange" class="mb-3" />

        <div v-if="selected">
          <!-- Mesmo motivo do bloco Geral: assignee_id atual, não histórico
          de quem atendeu. Desativado a pedido, sem remover a lógica.
          <ComparisonRow
            :label="t('REPORT_DASHBOARD.TEAM.CLIENTS_SERVED_TODAY')"
            :value="selected.atendidosDia"
            :percent="comparisons.atendidosDia"
            :hint="
              t('REPORT_DASHBOARD.TEAM.COLLABORATOR_HINTS.CLIENTS_SERVED_TODAY')
            "
          />
          -->
          <ComparisonRow
            :label="t('REPORT_DASHBOARD.TEAM.IN_PROGRESS')"
            :value="selected.emAtendimento"
            :percent="comparisons.emAtendimento"
            :hint="t('REPORT_DASHBOARD.TEAM.COLLABORATOR_HINTS.IN_PROGRESS')"
          />
          <ComparisonRow
            :label="t('REPORT_DASHBOARD.TEAM.RESOLVED_CONVERSATIONS')"
            :value="selected.finalizados"
            :percent="comparisons.finalizados"
            :hint="
              t(
                'REPORT_DASHBOARD.TEAM.COLLABORATOR_HINTS.RESOLVED_CONVERSATIONS'
              )
            "
          />
          <ComparisonRow
            :label="t('REPORT_DASHBOARD.TEAM.RESOLVED_BY_CONVERSATIONS')"
            :value="selected.finalizadosPor"
            :percent="comparisons.finalizadosPor"
            :hint="
              t(
                'REPORT_DASHBOARD.TEAM.COLLABORATOR_HINTS.RESOLVED_BY_CONVERSATIONS'
              )
            "
          />
          <LabelValueRow
            :label="t('REPORT_DASHBOARD.TEAM.AVG_RESOLUTION_TIME')"
            :value="selected.tempoMedio"
            :hint="
              t('REPORT_DASHBOARD.TEAM.COLLABORATOR_HINTS.AVG_RESOLUTION_TIME')
            "
          />
        </div>
      </div>
    </div>

    <div
      class="absolute bottom-6 right-8 grid place-content-center size-9 bg-white rounded-lg"
    >
      <Logo class="size-5" />
    </div>
  </section>
</template>
